import { VirtualElement } from '@popperjs/core';
import * as d3 from 'd3';
import { FC, useLayoutEffect, useRef, useState } from 'react';
import { ContainerText } from 'src/components/atoms';
import { CasesOverTimeDataType } from 'src/graphql';
import { useCache, useElementDimensions } from 'src/hooks';
import {
  appendAxis,
  appendAxisAndTicks,
  appendStackedBarGraph,
  createBandScale,
  createColors,
  createDaysLine,
  createLinearScale,
  createNoDecimalAxisLeft,
  createXAxis,
} from 'src/utils';
import { DefaultTheme, useTheme } from 'styled-components';
import { ChartToolTip } from '../ChartToolTip';
import {
  BarStackedAndToolTipContainer,
  BarStackedContainer,
  ContainerTextContainer,
  CustomXBadge,
  CustomYBadge,
  GraphContainer,
  XAxisTitle,
  YAxisTitle,
} from './BarStacked.styles';

interface Props {
  xTitle: string;
  yTitle: string;
  show7And90Days: boolean;
  stackLabels: string[];
  data: CasesOverTimeDataType[];
  graphId?: string;
}
export const getStackedBarColors = (theme: DefaultTheme) => [
  theme.atlas.semantic.color.dataVisualization.chart.blue,
  theme.atlas.semantic.color.dataVisualization.chart.teal,
];
export const BarStacked: FC<Props> = ({
  graphId = 'stacked-bar',
  ...props
}: Props) => {
  const theme = useTheme();
  const stackedBarColors = getStackedBarColors(theme);
  const [coords, setCoords] = useState<[number, number] | null>(null);
  const [tooltipData, setTooltipData] = useState<
    (typeof props)['data'][0] | null
  >();
  const graph = useRef(null);
  const cache = useCache(props.data);

  const svgContainerRef = useRef<HTMLDivElement>(null);
  const { width: containerWidth } = useElementDimensions(svgContainerRef);

  const minWidth = 900;
  const height = 520;
  const tickInterval = 7;
  const tooltipPadding = 5;

  const graphWidth = Math.max(containerWidth, minWidth);
  const emptyState = props.data.every((item) => item.total === 0);
  const AXISLABELWIDTH = 100;

  const margin = { top: 30, right: 40, bottom: 40, left: 50 };

  const graphHeight = height - margin.top - margin.bottom;
  const y = (d: Props['data'][0]) => Number(d.total);
  const x = (d: Props['data'][0]) => Number(d.bar);

  useLayoutEffect(() => {
    const barGraph = d3.select(graph.current);
    barGraph.selectAll('*').remove();

    const cacheY = d3.map(cache ?? [], y);
    const X = d3.map(cache ?? [], x);
    const bars = d3.map(props.data, (d) => d.bar);
    const xDomainMax = Number(d3.max(X)) + tickInterval;
    const xTicks = props.data.map((i) => Number(i.bar));
    const yMax = Number(d3.max(cacheY));
    const yDomainMax = emptyState
      ? 50
      : (yMax > 5 ? Math.ceil(yMax / 5) * 5 : Math.ceil(yMax)) + yMax / 10;

    const { scale: xDisplayScale } = createLinearScale({
      startPosition: margin.left,
      endPosition: graphWidth - margin.right,
      domainMax: xDomainMax,
    });
    const { scale: yScale } = createLinearScale({
      startPosition: graphHeight - margin.bottom,
      endPosition: margin.top,
      domainMax: yDomainMax,
    });
    const { scale: xScale } = createBandScale({
      startPosition: margin.left,
      endPosition: graphWidth - margin.right,
      domain: bars,
      paddingRatio: 0.015,
    });
    const { Axis: yAxis } = createNoDecimalAxisLeft({
      axisScale: yScale,
      tickNumber: 5,
      tickSize: 0,
      tickPadding: 20,
    });
    const { colors } = createColors({
      colorRange: stackedBarColors,
      domain: props.stackLabels,
    });
    const { axis: xAxis } = createXAxis({
      axisScale: xDisplayScale,
      tickValues: xTicks,
      tickSize: 20,
      tickPadding: 10,
      tickSizeOuter: 0,
      tickFormat: (d) => (d === 98 ? '98+' : String(d)),
    });

    appendAxisAndTicks({
      graph: barGraph,
      transform: `translate(${margin.left},0)`,
      width: graphWidth,
      axis: yAxis,
      dashAxis: 'x2',
      theme,
    });
    appendStackedBarGraph({
      graph: barGraph,
      data: props.data,
      stackLabels: props.stackLabels,
      xScale: xScale,
      yScale: yScale,
      bars: bars,
      width: xScale.bandwidth(),
      colors: colors,
      graphId,
      setCoords,
      setTooltipData,
    });
    appendAxis({
      graph: barGraph,
      transform: `translate(0,${graphHeight - margin.bottom})`,
      axis: xAxis,
      theme,
    });
    if (props.show7And90Days) {
      createDaysLine({
        xValue: 7,
        graph: barGraph,
        pillWidth: 56,
        text: '7-Day',
        xScale: xDisplayScale,
        yScale: yScale,
        yPosition: yDomainMax,
        theme,
      });
      createDaysLine({
        xValue: 90,
        graph: barGraph,
        pillWidth: 65,
        text: '90-Day',
        xScale: xDisplayScale,
        yScale: yScale,
        yPosition: yDomainMax,
        theme,
      });
    }
  }, [
    cache,
    emptyState,
    graphHeight,
    graphId,
    graphWidth,
    margin.bottom,
    margin.left,
    margin.right,
    margin.top,
    props.data,
    props.show7And90Days,
    props.stackLabels,
    stackedBarColors,
    theme,
  ]);

  return (
    <BarStackedAndToolTipContainer>
      {emptyState && (
        <ContainerTextContainer
          width={graphWidth - AXISLABELWIDTH}
          marginLeft={AXISLABELWIDTH}
        >
          <ContainerText text="No Data" icon={true} />
        </ContainerTextContainer>
      )}
      <GraphContainer>
        <YAxisTitle>
          <CustomYBadge variant="new" text={props.yTitle} capitalize />
        </YAxisTitle>
        <BarStackedContainer ref={svgContainerRef}>
          <svg
            viewBox={`0 0 ${graphWidth} ${graphHeight}`}
            width={'100%'}
            preserveAspectRatio="none"
            height={`${graphHeight}px`}
            style={{ display: 'block' }}
          >
            <g ref={graph} />
          </svg>
        </BarStackedContainer>
      </GraphContainer>
      <XAxisTitle>
        <CustomXBadge variant="new" text={props.xTitle} capitalize />
      </XAxisTitle>
      {coords && tooltipData?.bar ? (
        <ChartToolTip
          root={
            {
              getBoundingClientRect: () => ({
                top: coords[1] - 185 - tooltipPadding,
                left: coords[0],
                width: 0,
                height: 0,
              }),
            } as VirtualElement
          }
          chartData={{
            title: 'Duration',
            titleCounter:
              tooltipData?.bar === '98'
                ? '98+'
                : `${tooltipData?.bar} - ${Number(tooltipData?.bar) + 6} days`,
            data: [
              { text: 'Cases open', counter: tooltipData.open },
              { text: 'Cases closed', counter: tooltipData.closed },
              { text: 'Total cases', counter: tooltipData.total },
            ],
          }}
        />
      ) : (
        <div />
      )}
    </BarStackedAndToolTipContainer>
  );
};
